import React, { Suspense, lazy, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Header from 'components/partials/Header';
import Footer from 'components/partials/Footer';
import Drawer from 'components/partials/Drawer';
import SocketController from 'containers/SocketController';
import LazyLoadingIndicator from 'components/LazyLoadingIndicator';
import cssVars from 'css-vars-ponyfill';

import API from 'services';
import history from 'utils/history';
import * as authActions from 'store/modules/auth';
import * as appActions from 'store/modules/app';
import * as walletActions from 'store/modules/wallet';
import * as marketActions from 'store/modules/market';
import * as branchActions from 'store/modules/branch';
import { PrivateRoute } from 'utils/routerExtension';
import ScrollToTop from 'utils/scrollToTop';
import 'App.scss';

import { IntlProvider, addLocaleData } from 'react-intl';
import ko from 'react-intl/locale-data/ko';
import en from 'react-intl/locale-data/en';
import zh from 'react-intl/locale-data/zh';
import customTranslation from 'locale';
import GA from 'utils/googleAnalytics'

addLocaleData([...en, ...ko, ...zh]);

const Home = lazy(() => import('containers/Home'));
const Trade = lazy(() => import('containers/trade'));
const Wallet = lazy(() => import('containers/wallet'));

const SwapIndex = lazy(() => import('containers/swap/index'));
const MyinvestIndex = lazy(() => import('containers/myinvest/index'));
const MypageIndex = lazy(() => import('containers/mypage/index'));
const CscenterIndex = lazy(() => import('containers/cscenter/index'));
const Login = lazy(() => import('containers/account/Login'));
const LoginOtp = lazy(() => import('containers/account/LoginOtp'));
const SignUp = lazy(() => import('containers/account/SignUp'));
const ChangePw = lazy(() => import('containers/account/ChangePw'));
const FindPw = lazy(() => import('containers/account/FindPw'));

const AboutTerms = lazy(() => import('containers/about/Terms'));
const AboutPrivacy = lazy(() => import('containers/about/Privacy'));

function App() {
  const dispatch = useDispatch();
  const language = useSelector(state => state.app.language);
  const token = useSelector(state => state.auth.token);
  const branchInfo = useSelector(state => state.branch);

  // get login info
  useEffect(() => {
    const logout = () => dispatch(authActions.logout());
    const login = (token, memberinfo) =>
      dispatch(authActions.login(token, memberinfo));
    const setMyAssets = assets => dispatch(walletActions.setMyAssets(assets));
    if (token !== null && typeof token !== 'undefined') {
      API.accounts()
        .checkToken()
        .then(res => {
          if (res.data.data.isValid) {
            API.accounts()
              .getMyBalance()
              .then(res => {
                login(token, res.data.data.member);
                setMyAssets(res.data.data.balance);
                //socket.login(res.data.data.member.socket_session_key)
              });
          } else {
            throw new Error();
          }
        })
        .catch(err => {
          // 로그아웃 처리
          logout();
        });
    }
  }, []);

  // get branch, market, wallet info
  useEffect(() => {
    const supportedLanguage = ['ko', 'en', 'zh'];
    const fallbackLanguage = 'en';
    const setLanguage = lang => dispatch(appActions.setLanguage(lang));
    const setBranchInfo = branchInfo =>
      dispatch(branchActions.setBranchInfo(branchInfo));
    const setWallet = v => dispatch(walletActions.setWallet(v));
    const setMarketList = marketList =>
      dispatch(marketActions.setMarketList(marketList));
    API.common()
      .getBranchInfo()
      .then(res => {
        const pmState = typeof res.data.data.partnerInfo !== "undefined" ? res.data.data.partnerInfo.pm_state : "";
        if(pmState === 'SERVER'){
          window.location.href = "https://static.unionex.market/system-maintenance.html";
        }else if(pmState === 'END'){
          window.location.href = "https://static.unionex.market/service-end.html";
        }else if(pmState === 'STOP'){
          window.location.href = "https://static.unionex.market/service-paused.html";
        }else if(res.data.data.menu_auth){
          setBranchInfo({
            ...res.data.data,
            partnerInfo:{
              ...res.data.data.partnerInfo,
              pm_state: 'COMPANY'
            }
          });  
        }else{
          setBranchInfo(res.data.data);
        }
        if(typeof res.data.data.partnerInfo !== 'undefined'){
          window.document.title = res.data.data.partnerInfo.pm_name;
          // 파트너 정보에 따라 사이트 기본 언어 세팅하기
          if (!window.localStorage.getItem('lang')) {
            setLanguage(
              supportedLanguage.indexOf(res.data.data.partnerInfo.pm_language) >
                -1
                ? res.data.data.partnerInfo.pm_language
                : fallbackLanguage
            );
          }
          // 파트너 테마 입히기
          cssVars({
            variables:{
            'primary': res.data.data.siteInfo.primaryColor,
            'primary-active': res.data.data.siteInfo.primaryActiveColor,
            'primary-text': res.data.data.siteInfo.primaryTextColor,
            'primary-text-active': res.data.data.siteInfo.primaryTextActiveColor,
            'secondary': res.data.data.siteInfo.secondaryColor,
            'secondary-active': res.data.data.siteInfo.secondaryActiveColor,
            'secondary-text': res.data.data.siteInfo.secondaryTextColor,
            'secondary-text-active': res.data.data.siteInfo.secondaryTextActiveColor,
            'highlight': res.data.data.siteInfo.highlightColor,
            }
          })
        }
      });

    API.market()
      .getMarketList()
      .then(res => {
        setMarketList(res.data);
      });

    API.wallet()
    .getWalletList()
    .then(res => {
      setWallet(res.data.wallet);
    });
  }, []);

  return (
    <div className="App">
      <IntlProvider locale={language} messages={customTranslation[language]}>
        {typeof branchInfo.pm_no === "undefined" ? <LazyLoadingIndicator /> : 
          <Router history={history}>
            { GA.init() && <GA.RouteTracker /> }
            <Header />
            <Suspense fallback={<LazyLoadingIndicator />}>
              <ScrollToTop>
                <Switch>
                  <Route exact path="/" component={Home} />
                  <Route exact path="/account/login" component={Login} />
                  <Route exact path="/account/login-otp" component={LoginOtp} />
                  <Route exact path="/account/signup" component={SignUp} />
                  <Route
                    exact
                    path="/join/repass/:session_token"
                    component={ChangePw}
                  />
                  <PrivateRoute path="/account/changepw" component={ChangePw} />
                  <Route exact path="/account/findpw" component={FindPw} />
                  <Route exact path="/cscenter" render={() => <Redirect to="/cscenter/notice" />} />
                  <Route exact path="/cscenter/guide" render={() => <Redirect to="/cscenter/guide/platform" />} />
                  <Route path="/cscenter" component={CscenterIndex} />
                  {branchInfo.partnerInfo.pm_state !== 'JOIN' ? 
                  <>
                    <Route exact path="/about/terms" component={AboutTerms} />
                    <Route exact path="/about/privacy" component={AboutPrivacy} />
                    <Route exact path="/about/privacy" component={AboutPrivacy} />
                    <Route
                      exact
                      path="/trade"
                      render={() => <Redirect to="/trade/usdt_btc" />}
                    />
                    <Route exact path="/trade/:pair" component={Trade} />
                    <Route
                      exact
                      path="/myinvest"
                      render={() => <Redirect to="/myinvest/holding" />}
                    />
                    <Route
                      exact
                      path="/mypage"
                      render={() => <Redirect to="/mypage/info" />}
                    />
                    <PrivateRoute path="/swap" component={SwapIndex} />
                    <PrivateRoute path="/myinvest" component={MyinvestIndex} />
                    <PrivateRoute path="/mypage" component={MypageIndex} />
                    <PrivateRoute exact path="/wallet" component={Wallet} />
                    <PrivateRoute path="/wallet/:coin/:type" component={Wallet} />
                  </> : undefined}
                </Switch>
              </ScrollToTop>
            </Suspense>
            <Footer />
            <Drawer />
            <SocketController />
          </Router>}
      </IntlProvider>
    </div>
  );
}

export default App;
